import { SettlementBonus } from '../models/settlement-query.model';
import { Settlement, SettlementLoadForm } from '../models/settlement.model';

export const MOCK_SETTLEMENT_LOAD_FORM: SettlementLoadForm = {
  providerRut: '96885930-3',
  providerName: 'Red Salud Providencia S.A.',
  defaultPaymentBranchId: 12,
  paymentBranches: [
    { id: 12, name: 'FORUM' },
    { id: 40, name: 'PROVIDENCIA' },
    { id: 55, name: 'SANTIAGO CENTRO' },
  ],
};

export const MOCK_STATUS_OPTIONS = [
  { id: 1, name: 'PROCESANDO' },
  { id: 2, name: 'POR VALIDAR' },
  { id: 3, name: 'POR VINCULAR' },
  { id: 4, name: 'EN PROCESO DE PAGO' },
  { id: 5, name: 'PAGO EMITIDO' },
  { id: 6, name: 'PAGO DESPACHADO' },
  { id: 7, name: 'ANULADA' },
  { id: 8, name: 'LIQUIDADA' },
] as const;

const MOCK_AFFILIATE_RUT = 'XX.XXX.852-3';
const MOCK_SERVICE_AMOUNT = 22626;
const INVALID_MESSAGE = 'El bono ingresado no es válido para este prestador.';

export interface MockSettlementRecord {
  settlement: Settlement;
  bonuses: SettlementBonus[];
}

const statusName = (statusId: number): string =>
  MOCK_STATUS_OPTIONS.find((option) => option.id === statusId)?.name ?? '';

// Folios terminados en 0 se simulan como no válidos para el prestador.
const toBonus = (folio: string, index: number, settlementId: number): SettlementBonus => {
  const valid = !folio.endsWith('0');
  return {
    bonusId: settlementId * 10 + index,
    folio,
    serviceAmount: valid ? MOCK_SERVICE_AMOUNT : 0,
    affiliateRut: valid ? MOCK_AFFILIATE_RUT : null,
    statusId: valid ? 1 : 2,
    status: valid ? 'VALIDO' : 'NO VALIDO',
    validationMessage: valid ? null : INVALID_MESSAGE,
  };
};

function buildRecord(
  settlementId: number,
  statusId: number,
  folios: readonly string[],
  overrides: Partial<Settlement> = {},
): MockSettlementRecord {
  const bonuses = folios.map((folio, index) => toBonus(folio, index, settlementId));
  const valid = bonuses.filter((bonus) => bonus.statusId === 1);
  return {
    bonuses,
    settlement: {
      settlementId,
      statusId,
      status: statusName(statusId),
      opaNumber: null,
      opaPaymentDate: null,
      linkDate: null,
      totalAmount: valid.reduce((sum, bonus) => sum + bonus.serviceAmount, 0),
      issueDate: new Date().toISOString(),
      providerRut: MOCK_SETTLEMENT_LOAD_FORM.providerRut,
      providerName: MOCK_SETTLEMENT_LOAD_FORM.providerName,
      validBonuses: valid.length,
      invalidBonuses: bonuses.length - valid.length,
      ...overrides,
    },
  };
}

const folioRange = (first: number, count: number): string[] =>
  Array.from({ length: count }, (_, index) => String(first + index * 3));

// Compartido entre Cargar Bonos y Liquidaciones Cargadas para que lo cargado aparezca en el listado.
export const mockSettlementRecords: MockSettlementRecord[] = [
  buildRecord(293675, 6, folioRange(6274001, 14), {
    opaNumber: 390946,
    opaPaymentDate: '2026-11-16T00:00:00.000Z',
    linkDate: '2026-10-16T00:00:00.000Z',
    issueDate: '2026-10-16T00:00:00.000Z',
  }),
  buildRecord(293674, 7, folioRange(6274101, 2), {
    totalAmount: null,
    issueDate: '2026-10-16T00:00:00.000Z',
  }),
  buildRecord(293690, 3, folioRange(6274201, 8), { issueDate: '2026-10-09T00:00:00.000Z' }),
  buildRecord(293701, 2, ['6274908', '6274911', '6274914'], {
    issueDate: '2026-10-12T00:00:00.000Z',
  }),
];

export function addMockSettlement(folios: readonly string[]): Settlement {
  const settlementId = Math.max(...mockSettlementRecords.map((r) => r.settlement.settlementId)) + 1;
  const record = buildRecord(settlementId, 2, folios);
  mockSettlementRecords.push(record);
  return record.settlement;
}
